// A photo the canvas shrinks keeps its camera, date taken and shot settings (#541, part of #380; iOS and Mac: #545). The canvas
// writes a new picture with no EXIF. Before the redraw we read the source's EXIF block, pass it through the allowlist
// (shared/src/exifAllow.ts) and write the rebuilt block into the new JPEG or WebP. The block is the allowlist's own bytes, written
// from checked values: no source byte is copied, and the GPS directory is not on the list. The Orientation tag is left out,
// because the canvas has already turned the pixels. A source with no EXIF, a block that does not parse, or a picture we cannot
// open gives no block and never a failed upload. The cleaner (stripLocation.ts) still runs on the result.
import { allowedExif, exifSegment } from "@bloom/shared";

/** How much of the start of a JPEG we read to find its Exif segment (it comes first; the segments are 64 KB at most). */
const JPEG_HEAD = 1024 * 1024;
/** A `meta` box bigger than this is not read. A 48 MP photo's is about 100 KB. */
const META_MAX = 4 * 1024 * 1024;
/** An Exif item bigger than this is not read. */
const ITEM_MAX = 1024 * 1024;
/** The most items we read in `iinf` and `iloc` (a 48 MP photo lists about 110 tiles before Exif), and extents of one item. */
const MAX_ITEMS = 4096;
const MAX_EXTENTS = 16;

const ascii = (b: Uint8Array, at: number, n: number) => String.fromCharCode(...b.subarray(at, at + n));
const bytesOf = async (b: Blob) => new Uint8Array(await b.arrayBuffer());
const concat = (parts: Uint8Array[]) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
};

/** The allowlist block for a picture drawn again (no Orientation), or undefined when none is left or the block is malformed. */
export function redrawExif(block: Uint8Array): Uint8Array | undefined {
  try {
    return allowedExif(block, { orientation: false });
  } catch {
    return undefined;
  }
}

// ---- Reading the source ----

/** The TIFF block of the first Exif segment of a JPEG. */
function jpegExif(b: Uint8Array): Uint8Array | undefined {
  if (b[0] !== 0xff || b[1] !== 0xd8) return;
  for (let at = 2; at + 4 <= b.length && b[at] === 0xff; ) {
    const marker = b[at + 1]!;
    if (marker === 0xff) {
      at++; // fill byte
      continue;
    }
    if (marker === 0xda || marker === 0xd9) return; // the picture starts: no Exif before it
    const len = (b[at + 2]! << 8) | b[at + 3]!;
    if (len < 2 || at + 2 + len > b.length) return;
    if (marker === 0xe1 && len >= 8 && ascii(b, at + 4, 6) === "Exif\0\0") return b.subarray(at + 10, at + 2 + len);
    at += 2 + len;
  }
}

interface Box {
  type: string;
  /** Where the box's data starts, and where it ends, in the array. */
  body: number;
  end: number;
}

function boxes(b: Uint8Array, from: number, to: number): Box[] {
  const out: Box[] = [];
  const dv = new DataView(b.buffer, b.byteOffset, b.length);
  for (let at = from; at + 8 <= to && out.length < MAX_ITEMS; ) {
    let size = dv.getUint32(at);
    let head = 8;
    if (size === 1) {
      if (at + 16 > to) break;
      size = dv.getUint32(at + 8) * 2 ** 32 + dv.getUint32(at + 12); // beyond 2^53 the end check below stops it
      head = 16;
    } else if (size === 0) size = to - at;
    if (size < head || at + size > to) break;
    out.push({ type: ascii(b, at + 4, 4), body: at + head, end: at + size });
    at += size;
  }
  return out;
}

/** The TIFF block of the Exif item of a HEIC or HEIF (found through `iinf` and `iloc`, as the reference reader does), or undefined. */
async function heifExif(file: Blob): Promise<Uint8Array | undefined> {
  // The top-level boxes are read from the start, box header by box header, until `meta`.
  let meta: Uint8Array | undefined;
  for (let at = 0, n = 0; at + 8 <= file.size && n < 64; n++) {
    const head = new DataView((await file.slice(at, at + 16).arrayBuffer()) as ArrayBuffer);
    let size = head.getUint32(0);
    const type = String.fromCharCode(head.getUint8(4), head.getUint8(5), head.getUint8(6), head.getUint8(7));
    if (size === 1 && head.byteLength >= 16) size = head.getUint32(8) * 2 ** 32 + head.getUint32(12);
    else if (size === 0) size = file.size - at;
    if (size < 8 || at + size > file.size) return;
    if (type === "meta") {
      if (size > META_MAX) return;
      meta = await bytesOf(file.slice(at, at + size));
      break;
    }
    at += size;
  }
  if (!meta) return;
  const dv = new DataView(meta.buffer, meta.byteOffset, meta.length);
  const hlen = dv.getUint32(0) === 1 ? 16 : 8;
  const kids = boxes(meta, hlen + 4, meta.length); // meta is a full box: 4 bytes of version and flags
  const iinf = kids.find((k) => k.type === "iinf");
  const iloc = kids.find((k) => k.type === "iloc");
  if (!iinf || !iloc) return;
  // The id of the Exif item.
  let exifId: number | undefined;
  const iv = meta[iinf.body]!;
  for (const infe of boxes(meta, iinf.body + 4 + (iv === 0 ? 2 : 4), iinf.end)) {
    if (infe.type !== "infe") continue;
    const ver = meta[infe.body]!;
    if (ver < 2) continue;
    const at = infe.body + 4;
    const id = ver === 2 ? dv.getUint16(at) : dv.getUint32(at);
    const typeAt = at + (ver === 2 ? 4 : 6);
    if (ascii(meta, typeAt, 4) === "Exif") {
      exifId = id;
      break;
    }
  }
  if (exifId === undefined) return;
  // iloc: where its data is.
  const ver = meta[iloc.body]!;
  let at = iloc.body + 4;
  const offsetSize = meta[at]! >> 4;
  const lengthSize = meta[at]! & 15;
  const baseSize = meta[at + 1]! >> 4;
  const indexSize = ver === 1 || ver === 2 ? meta[at + 1]! & 15 : 0;
  at += 2;
  const count = ver < 2 ? dv.getUint16(at) : dv.getUint32(at);
  at += ver < 2 ? 2 : 4;
  const num = (n: number) => {
    let v = 0;
    for (let i = 0; i < n; i++) v = v * 256 + meta![at + i]!;
    at += n;
    return v;
  };
  for (let i = 0; i < Math.min(count, MAX_ITEMS); i++) {
    if (at + 8 > iloc.end) return;
    const id = num(ver < 2 ? 2 : 4);
    const method = ver === 1 || ver === 2 ? num(2) & 15 : 0;
    at += 2; // data reference index
    const base = num(baseSize);
    const extents = num(2);
    if (id !== exifId) {
      if (extents > MAX_EXTENTS) return; // an item we do not read must not keep us busy either
      at += extents * (indexSize + offsetSize + lengthSize);
      continue;
    }
    if (method !== 0 || extents < 1 || extents > MAX_EXTENTS) return; // only data in the file itself
    const parts: Uint8Array[] = [];
    let total = 0;
    for (let j = 0; j < extents; j++) {
      if (at + indexSize + offsetSize + lengthSize > iloc.end) return;
      at += indexSize;
      const off = num(offsetSize);
      const len = num(lengthSize);
      total += len;
      if (total > ITEM_MAX || base + off + len > file.size) return;
      parts.push(await bytesOf(file.slice(base + off, base + off + len)));
    }
    const item = concat(parts);
    // The item: a 4-byte offset to the TIFF header (after those 4 bytes), then the data.
    if (item.length < 8) return;
    const skip = new DataView(item.buffer, item.byteOffset, item.length).getUint32(0);
    return 4 + skip < item.length ? item.subarray(4 + skip) : undefined;
  }
}

/** The EXIF block (TIFF bytes) of a JPEG, or of a HEIC or HEIF the browser can decode, or undefined. Never throws. */
export async function sourceExif(file: Blob): Promise<Uint8Array | undefined> {
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (head[0] === 0xff && head[1] === 0xd8) return jpegExif(await bytesOf(file.slice(0, JPEG_HEAD)));
    if (head.length >= 12 && ascii(head, 4, 4) === "ftyp") return await heifExif(file);
  } catch {
    // too short or malformed: no block
  }
}

// ---- Writing the new picture ----

/** A JPEG with `tiff` as its Exif segment: after JFIF if it has one, else right after the start marker (where the cleaner puts it). */
function jpegWith(jpeg: Uint8Array, tiff: Uint8Array): Uint8Array | undefined {
  if (jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return;
  let at = 2;
  if (jpeg[2] === 0xff && jpeg[3] === 0xe0 && jpeg.length >= 6) {
    const end = 4 + ((jpeg[4]! << 8) | jpeg[5]!);
    if (end <= jpeg.length) at = end;
  }
  return concat([jpeg.subarray(0, at), exifSegment(tiff), jpeg.subarray(at)]);
}

const u32le = (v: number) => new Uint8Array([v & 255, (v >>> 8) & 255, (v >>> 16) & 255, (v >>> 24) & 255]);
const u24le = (v: number) => new Uint8Array([v & 255, (v >>> 8) & 255, (v >>> 16) & 255]);
const tag = (s: string) => new Uint8Array([...s].map((c) => c.charCodeAt(0)));

/** The width and height in the frame header of a plain WebP's first chunk (`VP8 `: a key frame, RFC 6386 section 9.1; `VP8L`: the signature byte, then 14 bits each of width - 1 and height - 1), or undefined. */
export function frameSize(webp: Uint8Array, kind: string): [number, number] | undefined {
  if (kind === "VP8 ") {
    if (webp.length < 30 || (webp[20]! & 1) !== 0 || webp[23] !== 0x9d || webp[24] !== 0x01 || webp[25] !== 0x2a) return;
    return [(webp[26]! | (webp[27]! << 8)) & 0x3fff, (webp[28]! | (webp[29]! << 8)) & 0x3fff];
  }
  if (webp.length < 25 || webp[20] !== 0x2f) return;
  return [1 + ((webp[21]! | (webp[22]! << 8)) & 0x3fff), 1 + (((webp[22]! >> 6) | (webp[23]! << 2) | (webp[24]! << 10)) & 0x3fff)];
}

/** A WebP with `tiff` as its EXIF chunk. A file with a VP8X header (what Chromium's canvas writes, with ICCP) gets the flag and the chunk; a plain file (one VP8 or VP8L chunk) gets a VP8X header first. */
function webpWith(webp: Uint8Array, tiff: Uint8Array, width: number, height: number): Uint8Array | undefined {
  if (webp.length < 20 || ascii(webp, 0, 4) !== "RIFF" || ascii(webp, 8, 4) !== "WEBP") return;
  const first = ascii(webp, 12, 4);
  const exif = concat([tag("EXIF"), u32le(tiff.length), tiff, new Uint8Array(tiff.length & 1)]);
  let body: Uint8Array;
  if (first === "VP8X") {
    if (webp.length < 30) return;
    const head = webp.slice(12, 30);
    if ((head[8]! & 0x08) !== 0) return; // it has an EXIF chunk already
    head[8] = head[8]! | 0x08;
    body = concat([tag("WEBP"), head, webp.subarray(30), exif]);
  } else if (first === "VP8 " || first === "VP8L") {
    if (width < 1 || height < 1 || width > 2 ** 24 || height > 2 ** 24) return;
    // The canvas size is the frame's size or no client can decode the picture: a frame that says another size, or has no readable header, stays as it is.
    const frame = frameSize(webp, first);
    if (!frame || frame[0] !== width || frame[1] !== height) return;
    // A lossless picture may use its alpha channel (bit 28 of the header after the signature byte): the VP8X header must say so.
    const alpha = first === "VP8L" && webp.length >= 25 && ((webp[24]! >> 4) & 1) === 1 ? 0x10 : 0;
    const vp8x = concat([tag("VP8X"), u32le(10), new Uint8Array([0x08 | alpha, 0, 0, 0]), u24le(width - 1), u24le(height - 1)]);
    body = concat([tag("WEBP"), vp8x, webp.subarray(12), exif]);
  } else return;
  return concat([tag("RIFF"), u32le(body.length), body]);
}

/**
 * `out`, a picture the canvas wrote from `source`, with the camera, date and settings of `source` in it. The original `out` when
 * the source has none, when nothing is left after the allowlist, when the block is malformed, or when `out` is neither a JPEG
 * nor a WebP we can add to. Never throws.
 */
export async function keepExif(source: Blob, out: Blob, width: number, height: number): Promise<Blob> {
  try {
    const block = await sourceExif(source);
    const tiff = block && redrawExif(block);
    if (!tiff) return out;
    const bytes = await bytesOf(out);
    const made = out.type === "image/jpeg" ? jpegWith(bytes, tiff) : out.type === "image/webp" ? webpWith(bytes, tiff, width, height) : undefined;
    return made ? new Blob([made as Uint8Array<ArrayBuffer>], { type: out.type }) : out;
  } catch {
    return out;
  }
}
